import React, { useEffect } from 'react';
import {
  Clear,
  Close,
  Search,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Breakpoint,
  IconButton,
  Popover,
  styled,
  TextField,
  Theme,
  useMediaQuery
} from '@mui/material';
import {
  GridInitialState,
  GridToolbarQuickFilterProps,
  gridQuickFilterValuesSelector,
  useGridApiContext
} from '@mui/x-data-grid-pro';
import debounce from 'lodash/debounce';
import {
  bindPopover,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';

export const gridInitialState = {} as Record<string, GridInitialState>;

/**
 * This component is used to render the quick filter input field in the DataGrid
 * toolbar. It is responsive and will render a popover on mobile devices. It
 * also uses the `useGridApiContext` hook to access the `setQuickFilterValues`,
 * so it must rendered within the `DataGridPro` context.
 */
export const GridToolbarQuickFilter: React.FC<
  GridToolbarQuickFilterProps & {
    defaultSearch?: string;
    color?: 'primary' | 'secondary';
    placeholder?: string;
    onSearchCapture?: () => void;
    popoverBreakpoint?: Breakpoint;
    collapsed?: boolean;
  }
> = (props) => {
  const {
    onSearchCapture,
    popoverBreakpoint = 'md',
    collapsed,
    ...textFieldProps
  } = props;
  const gridApiRef = useGridApiContext();
  const quickFilterValues =
    (gridQuickFilterValuesSelector(gridApiRef)?.[0] as string | undefined) ??
    '';
  const [focused, setFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(quickFilterValues);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'quickFilterPopover'
  });
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isBelowPopoverBreakpoint = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(popoverBreakpoint)
  );
  const isSearchCollapsed = collapsed ?? isBelowPopoverBreakpoint;
  const debouncedSetQuickFilterValues = React.useMemo(
    () =>
      debounce((value: string) => {
        gridApiRef.current.setQuickFilterValues(
          value.length > 0 ? [value] : []
        );
        if (!isSearchCollapsed && value.trim().length > 0) {
          onSearchCapture?.();
        }
      }, 500),
    [gridApiRef, onSearchCapture, isSearchCollapsed]
  );

  React.useEffect(() => {
    if (popupState.isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [popupState]);
  React.useEffect(() => {
    setInputValue(quickFilterValues);
  }, [quickFilterValues]);
  React.useEffect(() => {
    return () => debouncedSetQuickFilterValues.cancel();
  }, [debouncedSetQuickFilterValues]);
  const handleSubmit = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    debouncedSetQuickFilterValues.cancel();
    gridApiRef.current.setQuickFilterValues(
      inputValue.length > 0 ? [inputValue] : []
    );
    if (inputValue.trim().length > 0) {
      onSearchCapture?.();
    }
    popupState.close();
  };
  const handleClose = () => {
    handleReset();
    popupState.close();
  };
  const handleReset = () => {
    debouncedSetQuickFilterValues.cancel();
    setInputValue('');
    gridApiRef.current.setQuickFilterValues([]);
  };

  useEffect(() => {
    if (!textFieldProps.defaultSearch) {
      return;
    }
  }, [textFieldProps.defaultSearch]);

  const bindedTextField = (
    <StyledTextField
      {...textFieldProps}
      value={inputValue}
      className={focused ? 'focused' : ''}
      data-testid="search-bar-input"
      onChange={(e) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedSetQuickFilterValues(value);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      variant="standard"
      inputRef={inputRef}
      InputProps={{
        id: 'search-bar',
        startAdornment: (
          <SearchIcon
            htmlColor="#4E616D"
            sx={{ marginRight: '10px', height: '0.8em', width: '0.8em' }}
          />
        ),
        endAdornment: (quickFilterValues?.length ?? 0) > 0 && (
          <Clear
            sx={{
              width: '18px',
              height: '18px',
              color: 'var(--palette-semantic-icon-quaternary)',
              cursor: 'pointer'
            }}
            onClick={() => handleReset()}
          />
        ),
        autoCapitalize: 'off',
        autoCorrect: 'off',
        autoComplete: 'off',
        disableUnderline: true,
        placeholder: textFieldProps.placeholder || 'Search'
      }}
    />
  );

  return isSearchCollapsed ? (
    <>
      <StyledSearchButton
        color={textFieldProps.color}
        aria-label="Search"
        data-testid="search-bar-button"
        {...bindTrigger(popupState)}
        hasSearchQuery={(quickFilterValues?.length ?? 0) > 0 ? true : false}
      >
        <Search
          sx={{
            width: '24px !important',
            height: '24px !important'
          }}
          fontSize="small"
          htmlColor={
            textFieldProps.color === 'secondary'
              ? 'var(--palette-semantic-icon-tertiary)'
              : 'var(--palette-semantic-icon-quaternary)'
          }
        />
      </StyledSearchButton>
      <StyledPopover {...bindPopover(popupState)}>
        <form onSubmit={handleSubmit}>{bindedTextField}</form>
        <IconButton onClick={handleClose} size="small">
          <Close
            htmlColor={
              textFieldProps.color === 'secondary'
                ? 'var(--palette-semantic-icon-tertiary)'
                : 'var(--palette-semantic-icon-quaternary)'
            }
          />
        </IconButton>
      </StyledPopover>
    </>
  ) : (
    bindedTextField
  );
};

const hasSearchQueryMixin = `
  background: var(--palette-semantic-background-base);
  border: 1px solid var(--palette-semantic-border-primary);
  box-shadow: 0px 0px 5px var(--palette-primitive-blue-900);
`;

export const StyledSearchButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'hasSearchQuery' && prop !== 'color'
})<{
  hasSearchQuery: boolean;
  color?: 'primary' | 'secondary';
}>`
  background: ${({ color }) =>
    color === 'secondary'
      ? 'transparent'
      : 'var(--palette-semantic-background-secondary)'};
  width: 42px;
  height: 42px;
  ${({ hasSearchQuery }) => hasSearchQuery && hasSearchQueryMixin}
`;

export const StyledPopover = styled(Popover)`
  & .MuiPaper-root {
    width: 300px;
    height: 82px;
    box-shadow: 0px 5px 20px 2px rgba(44, 58, 66, 0.15);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 20px;
    padding-right: 8px;
  }
`;

export const StyledTextField = styled(TextField)`
  transition: all 0.15s ease-in-out;
  width: 220px;
  height: 42px;
  border-radius: 1000px;
  padding: 5px 14px 0 12px;
  color: #20333e;
  filter: drop-shadow(0px 5px 10px rgba(117, 136, 153, 0.1));
  background: var(--palette-semantic-background-secondary);
  border: 1px solid var(--palette-semantic-divider-primary);
  &.focused {
    border: 1px solid var(--palette-semantic-border-primary);
    background: #fcfeff;
  }
  & input {
    font-size: 14px;
    margin: 1px 0 0 0;
    ::placeholder {
      color: var(--palette-semantic-text-secondary);
      opacity: 0.75;
      font-size: 14px;
    }
  }
`;
